/*
  The gallery as an input to the variant picker.

  Dawn already drives the gallery from the picker: choosing a variant
  re-renders the section, and product-info.js brings that variant's photograph
  to the front of the gallery. It has never done the other direction - the
  gallery is an output of the picker and never an input to it. On a phone,
  where the gallery fills the first screen and the picker sits below it, that
  means a customer can swipe through every variant's photograph while the
  price, the availability and the add to cart button all keep describing the
  variant they started on.

  This listens for the gallery landing on a new image, checks that image's
  variant in the picker's own inputs, and fires the `change` event Dawn is
  already listening for. Everything downstream - the section fetch, the price,
  the submit button, the URL, the gallery itself - is Dawn's code doing exactly
  what it does when someone taps a chip. Nothing here re-implements variant
  selection; it only presses Dawn's buttons.

  Two things it deliberately does not do:

  - It selects nothing for a photograph that belongs to no variant. A lifestyle
    shot is not a choice, and swiping past one must not change what is in the
    cart.
  - It does not touch `setActiveMedia`, or any other Dawn method. An earlier
    version wrapped it to suppress the reorder Dawn does when the chosen
    variant's image is not already leading the gallery. That wrapper could
    never do what it claimed: with `hide_variants` off, the gallery carries
    every variant's image and the server renders the chosen one first, so the
    reorder comes from `updateMedia` matching the new order - not from the
    `prepend` argument the wrapper was overriding. The photograph on screen is
    the one the customer swiped to either way.

  Requires `hide_variants` to be off, and main-product.liquid only emits the
  map and this script when it is. With it on, Dawn renders one variant image -
  the selected variant's - so there is no slide for any other variant to be
  reached from, and this has nothing to map.
*/
if (!window.nldGalleryVariantSync) {
  window.nldGalleryVariantSync = true;

  /* Local rather than global.js's `debounce`, so this file does not depend on
     which of two deferred scripts the browser runs first. */
  const debounce = (fn, wait) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  const wire = (productInfo) => {
    const gallery = productInfo.querySelector('media-gallery');
    const mapScript = productInfo.querySelector('[data-variant-media]');
    if (!gallery || !mapScript || gallery.dataset.nldVariantSync === 'on') return;

    let entries;
    try {
      entries = JSON.parse(mapScript.textContent);
    } catch (error) {
      return;
    }
    if (!Array.isArray(entries) || entries.length === 0) return;

    gallery.dataset.nldVariantSync = 'on';

    /* The gallery spells media ids `${sectionId}-${mediaId}`; the map is keyed
       on the bare id, because the section id changes between the product page
       and a quick view of the same product. */
    const prefix = `${productInfo.dataset.section}-`;

    /* One photograph can belong to several variants - two colourways shot on
       the same body, a size run sharing one product shot - so the map is one
       to many. */
    const byMedia = new Map();
    entries.forEach((entry) => {
      const key = String(entry.media);
      const bucket = byMedia.get(key);
      if (bucket) {
        bucket.push(entry);
      } else {
        byMedia.set(key, [entry]);
      }
    });

    const variantsForMedia = (galleryMediaId) => {
      const id = String(galleryMediaId);
      return byMedia.get(id.startsWith(prefix) ? id.slice(prefix.length) : id) || [];
    };

    /* One group per option, in the order the values are listed in
       `entry.options` - which is the order `options_with_values` renders the
       groups in. */
    const optionGroups = (picker) => picker.querySelectorAll('fieldset, .product-form__input--dropdown');

    const chosenIn = (group) => {
      const dropdown = group.querySelector('select');
      if (dropdown) return dropdown.value;
      return group.querySelector('input[type="radio"]:checked')?.value ?? null;
    };

    const alreadySelected = (picker, options) =>
      Array.from(optionGroups(picker)).every((group, index) => {
        const wanted = options[index];
        if (wanted == null) return true;
        return chosenIn(group) === wanted;
      });

    const selectVariantForMedia = (galleryMediaId) => {
      const candidates = variantsForMedia(galleryMediaId);
      if (candidates.length === 0) return;

      const picker = productInfo.querySelector('variant-selects');
      if (!picker) return;

      /* This photograph already describes what is in the picker - which is the
         case every time Dawn moves the gallery in response to a pick, and every
         time two variants share a shot and one of them is the chosen one.
         Returning here is what keeps the two directions from driving each
         other in a loop. */
      if (candidates.some((entry) => alreadySelected(picker, entry.options))) return;

      const { options } = candidates[0];
      let lastChanged = null;

      optionGroups(picker).forEach((group, index) => {
        const wanted = options[index];
        if (wanted == null) return;

        const dropdown = group.querySelector('select');
        if (dropdown) {
          if (dropdown.value !== wanted) {
            dropdown.value = wanted;
            lastChanged = dropdown;
          }
          return;
        }

        const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find((input) => input.value === wanted);
        if (radio && !radio.checked) {
          radio.checked = true;
          lastChanged = radio;
        }
      });

      /* Setting every group before dispatching matters: Dawn reads all of the
         picker's checked inputs when the event fires, so an event per group
         would cost a section fetch per group. */
      lastChanged?.dispatchEvent(new Event('change', { bubbles: true }));
    };

    /* A fast swipe crosses several slides; Dawn debounces its own slide
       handler by 500ms for the same reason. This is shorter because a variant
       selection is what the customer is waiting to see, but still long enough
       that passing over an image does not cost a section fetch. */
    const onSlide = debounce((event) => {
      const mediaId = event.detail?.currentElement?.dataset?.mediaId;
      if (mediaId) selectVariantForMedia(mediaId);
    }, 300);

    gallery.querySelector('[id^="GalleryViewer"]')?.addEventListener('slideChanged', onSlide);

    /* Thumbnails call `setActiveMedia` directly and raise no slide event, so
       they are read off the click instead. Only some gallery layouts render
       them; `stacked` with mobile thumbnails hidden renders none. */
    gallery.querySelector('[id^="GalleryThumbnails"]')?.addEventListener('click', (event) => {
      const thumbnail = event.target.closest('[data-target]');
      if (thumbnail) selectVariantForMedia(thumbnail.dataset.target);
    });

    /* Arriving on `?variant=`, or on a product whose first available variant is
       not the first photograph. A fixed order leaves the chosen variant's image
       wherever the product puts it rather than moving it to the front, so the
       gallery has to open on it instead. One frame's wait is for the slides to
       have their width; landing here selects nothing, because the variant this
       slide belongs to is the one already in the picker. */
    if (gallery.hasAttribute('data-fixed-order')) {
      requestAnimationFrame(() => {
        const slider = gallery.querySelector('[id^="Slider-Gallery"]');
        const active = gallery.querySelector('[id^="GalleryViewer"] li.is-active[data-media-id]');
        if (slider && active && slider.scrollLeft !== active.offsetLeft) {
          slider.scrollTo({ left: active.offsetLeft, behavior: 'instant' });
        }
      });
    }
  };

  const wireAll = () => document.querySelectorAll('product-info').forEach(wire);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireAll);
  } else {
    wireAll();
  }

  /* A combined-listing swap replaces product-info, so the element announces
     itself again on the way back in. */
  document.addEventListener('product-info:loaded', ({ target }) => wire(target));
}
